import './AddMappingModal.scss';
import { singularize } from 'inflected';
import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { AssignMappingItem } from '@blisspointmedia/bpm-types/dist/ClientDataMapping';
import { IsoString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { useSetError } from '../../../redux/modals';
import { NO_AUTOFILL_PROPS } from '../../../utils/form-utils';
import { assignMappingItems } from '../client-data-mapping-api';
import { MappingTabConfig } from '../client-data-mapping-constants';
import { isValidSharedDriveId } from '../client-data-mapping-utils';
import { useClientTargetPicker } from '../ClientTargetPicker/ClientTargetPicker';

interface AddMappingModalProps {
  tab: MappingTabConfig;
  // Set on the Client-Level page (always this company); left undefined on the Global Admin
  // page, where the modal shows a company picker instead.
  fixedCid?: string;
  onHide: () => void;
  onAdded: () => void;
  // Optional duplicate check: called with the normalized external ID on blur. When it returns
  // true the field is marked invalid and Add is disabled. Only needed for types where the
  // backend would silently reuse an existing row (slack_channel: the workspace mirror means
  // channels can exist without an oracle_slack_channels row, so the user must reassign).
  existsCheck?: (externalId: string) => boolean;
}

export const AddMappingModal = ({
  tab,
  fixedCid,
  onHide,
  onAdded,
  existsCheck
}: AddMappingModalProps) => {
  const setError = useSetError();
  // Every per-type UI behavior lives on the tab config; destructure for shorthand access so
  // this component doesn't need to know that "email_domain" is any different from the others.
  const {
    label,
    hasCallDate,
    manualInput,
    nameColumnLabel = 'Display Name',
    externalIdColumnLabel = 'External ID'
  } = tab;
  // Field labels default to the table's per-tab column labels so the modal reads the same way
  // as the row the user just clicked from. manualInput.label wins when a tab wants a distinct
  // input-side label (email_domain: "Domain" for a field that also derives the display name).
  const externalIdFieldLabel = manualInput.label ?? externalIdColumnLabel;
  const displayNameFieldLabel = nameColumnLabel;
  // Shared client + generic picker -- owns the cids-array and markGeneric state, plus the OR
  // wrapper layout on allowGeneric tabs. See ClientTargetPicker.
  const target = useClientTargetPicker({
    tab,
    fixedCid,
    checkboxId: 'add-mapping-mark-generic'
  });
  const [manualExternalId, setManualExternalId] = useState(manualInput.prefillValue ?? '');
  const [manualDisplayName, setManualDisplayName] = useState('');
  const [manualCallDate, setManualCallDate] = useState('');
  const [manualSharedDriveId, setManualSharedDriveId] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const normalizeInput = manualInput.normalize ?? ((raw: string) => raw.trim());
  const normalizedExternalId = normalizeInput(manualExternalId);
  const validate = manualInput.validate ?? (() => true);
  const externalIdValid = validate(normalizedExternalId);
  const trimmedSharedDriveId = manualSharedDriveId.trim();
  const sharedDriveIdValid =
    !manualInput.requiresSharedDriveId || isValidSharedDriveId(trimmedSharedDriveId);
  // Only mark the input visibly invalid once the user has typed past the prefill -- avoids a
  // red border on the '@' prefill the moment the email_domain modal opens.
  const hasTypedPastPrefill =
    manualExternalId !== (manualInput.prefillValue ?? '') && manualExternalId.trim().length > 0;

  const handleExternalIdChange = (value: string) => {
    setManualExternalId(value);
    // Clear the duplicate flag whenever the user edits the field so they aren't stuck with
    // a stale error while typing a new value.
    setIsDuplicate(false);
  };

  const handleExternalIdBlur = () => {
    if (!existsCheck || !normalizedExternalId) return;
    setIsDuplicate(existsCheck(normalizedExternalId));
  };

  const canSave = Boolean(
    target.isValid &&
    externalIdValid &&
    !isDuplicate &&
    manualExternalId.trim() &&
    (manualInput.derivesDisplayName || manualDisplayName.trim()) &&
    sharedDriveIdValid
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const externalId = normalizedExternalId;
      const displayName = manualInput.derivesDisplayName ? externalId : manualDisplayName.trim();
      const item: AssignMappingItem = {
        externalId,
        displayName,
        // call_date carries the actual call start time (Oracle sync populates it that way),
        // so the datetime-local input's local-time value needs converting to a real ISO
        // timestamp rather than sent as-is. Date#toISOString() always produces a valid
        // IsoString at runtime; the cast is just because TS can't verify that statically.
        callDate:
          hasCallDate && manualCallDate
            ? (new Date(manualCallDate).toISOString() as IsoString)
            : undefined,
        sharedDriveId: manualInput.requiresSharedDriveId ? trimmedSharedDriveId : undefined
      };
      // target.targetCids is either [GENERIC_CID] (markGeneric) or the picked-client array.
      // Fan out one assign call per target -- the backend applies each in its own transaction
      // so partial failures don't roll back the rest.
      const results = await Promise.all(
        target.targetCids.map(c => assignMappingItems(c, tab.key, [item]))
      );
      const failed = results.flat().filter(r => !r.success);
      if (failed.length) {
        setError({
          message: `Failed to add ${failed.length} of ${target.targetCids.length} assignment${
            target.targetCids.length === 1 ? '' : 's'
          }: ${
            failed
              .map(f => f.message)
              .filter(Boolean)
              .join('; ') || 'unknown error'
          }`
        });
        // Total failure -- leave modal open so the user can retry.
        if (failed.length === target.targetCids.length) return;
      }
      onAdded();
      onHide();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  const singularLabel = singularize(label);

  return (
    <Modal show onHide={onHide} centered className='add-mapping-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Add {singularLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className='add-mapping-field' {...NO_AUTOFILL_PROPS}>
          <div className='add-mapping-field-label-row'>
            <Form.Label className='mb-0'>{externalIdFieldLabel}</Form.Label>
            {manualInput.labelTooltip && <InfoTooltip>{manualInput.labelTooltip}</InfoTooltip>}
          </div>
          <Form.Control
            value={manualExternalId}
            onChange={e => handleExternalIdChange(e.target.value)}
            onBlur={handleExternalIdBlur}
            placeholder={manualInput.placeholder}
            isInvalid={(hasTypedPastPrefill && !externalIdValid) || isDuplicate}
          />
          {isDuplicate && (
            <Form.Control.Feedback type='invalid'>
              This channel already exists. Find it in the Unassigned or Assigned tab and edit it
              there.
            </Form.Control.Feedback>
          )}
          {manualInput.helpText && <Form.Text muted>{manualInput.helpText}</Form.Text>}
        </Form.Group>
        {!manualInput.derivesDisplayName && (
          <Form.Group className='add-mapping-field'>
            <Form.Label>{displayNameFieldLabel}</Form.Label>
            <Form.Control
              data-testid='add-mapping-display-name'
              value={manualDisplayName}
              onChange={e => setManualDisplayName(e.target.value)}
            />
          </Form.Group>
        )}
        {hasCallDate && (
          <Form.Group className='add-mapping-field'>
            <Form.Label>Call Date &amp; Time</Form.Label>
            <Form.Control
              type='datetime-local'
              value={manualCallDate}
              onChange={e => setManualCallDate(e.target.value)}
            />
          </Form.Group>
        )}
        {manualInput.requiresSharedDriveId && (
          <Form.Group className='add-mapping-field'>
            <div className='add-mapping-field-label-row'>
              <Form.Label className='mb-0'>Shared Drive ID</Form.Label>
              {manualInput.sharedDriveIdTooltip && (
                <InfoTooltip>{manualInput.sharedDriveIdTooltip}</InfoTooltip>
              )}
            </div>
            <Form.Control
              value={manualSharedDriveId}
              onChange={e => setManualSharedDriveId(e.target.value)}
              placeholder='Shared Drive ID'
              isInvalid={Boolean(trimmedSharedDriveId) && !sharedDriveIdValid}
            />
          </Form.Group>
        )}
        {target.render()}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!canSave} loading={saving} onClick={handleSave}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
